import { Icon } from "./Icon.js";
import { resolveStatus, type Cluster, type DeviceProbe } from "./ClusterCard.js";
import type { TelemetrySnapshot } from "../api/telemetry.js";
import { useI18n } from "../i18n.js";

/**
 * La vista SITE MAP, portata da `Home.qml`.
 *
 * Non e' un secondo modo di far vedere le stesse schede: e' la **tabella
 * dell'impianto**. Nella griglia si guarda il video e si capisce cosa sta
 * succedendo; qui si legge la configurazione — soglie e indirizzi di camera,
 * sensore e relè — che e' quello che serve quando qualcosa non risponde e si
 * deve dire a un tecnico dove guardare.
 *
 * Stato e concentrazione restano dal vivo anche qui, dalla stessa telemetria
 * che colora le schede: due strade diverse per lo stesso numero darebbero due
 * verita' che divergono, e sarebbe questa a mentire.
 */
export function SiteMap({
  clusters,
  telemetry,
  probe,
  onOpen,
}: {
  clusters: Cluster[];
  telemetry: TelemetrySnapshot;
  probe: Record<string, DeviceProbe>;
  onOpen: (c: Cluster) => void;
}) {
  const { t } = useI18n();

  return (
    <div className="sitemap">
      <table className="table sitemap__table">
        <thead>
          <tr>
            <th>{t("sitemap.status")}</th>
            <th>{t("sitemap.cluster")}</th>
            <th>{t("sitemap.area")}</th>
            <th className="ta-right">PPM·M</th>
            <th>{t("sitemap.thresholds")}</th>
            <th>{t("hdr.cameraIp")}</th>
            <th>{t("hdr.sensorIp")}</th>
            <th>{t("hdr.dcsIp")}</th>
            <th className="ta-right">{t("sitemap.focus")}</th>
          </tr>
        </thead>
        <tbody>
          {clusters.map((c) => {
            const status = resolveStatus(c, telemetry[c.id], probe[c.id]);
            const tone = status.toLowerCase();
            const ppm = telemetry[c.id]?.ppm ?? c.currentPpm ?? undefined;
            return (
              <tr key={c.id}>
                <td>
                  <span className={`sitemap__st sitemap__st--${tone}`}>
                    <i className="dot dot--live" />
                    {status}
                  </span>
                </td>
                <td className="sitemap__name">{c.name}</td>
                <td className="mono muted">{c.areaCode ?? ""}</td>
                {/* Niente zero di comodo: un trattino dice "non sta
                    leggendo", uno zero direbbe "legge zero". */}
                <td className={`ta-right sitemap__ppm sitemap__ppm--${tone}`}>
                  {ppm === undefined ? "—" : ppm.toFixed(1)}
                </td>
                {/* Badge quadrati e non pastiglie tonde: nel .qml sono
                    rettangoli raggio 4 alti 20, e in una tabella fitta le
                    pastiglie tonde rubano larghezza alle colonne che seguono. */}
                <td className="sitemap__thr">
                  <span className="cbadge cbadge--l1">L1: {c.thresholdL1 ?? "—"}</span>
                  <span className="cbadge cbadge--l2">L2: {c.thresholdL2 ?? "—"}</span>
                </td>
                <Addr icon="camera" value={c.cameraIp} />
                <Addr icon="gauge" value={c.sensorIp} />
                <Addr icon="wifi" value={c.moxaIp} />
                <td className="ta-right">
                  <button className="sitemap__focus" onClick={() => onOpen(c)}>
                    {t("sitemap.focus")}
                  </button>
                </td>
              </tr>
            );
          })}
          {clusters.length === 0 && (
            <tr>
              <td colSpan={9} className="muted">
                {t("login.systemStatusNoClusters")}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function Addr({ icon, value }: { icon: string; value: string | null | undefined }) {
  return (
    <td className="mono sitemap__addr">
      {value ? (
        <>
          <Icon name={icon} size={12} />
          {value}
        </>
      ) : (
        <span className="sitemap__none">—</span>
      )}
    </td>
  );
}
